/**
 * Publiczny formularz „Problem z zamówieniem na dowóz".
 * Route: /reklamacja/:location
 *
 * Premium dark / gold aesthetic, mobile-first, glass-morphism.
 * Logika i endpointy pozostają nietknięte — jedynie warstwa wizualna.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  PackageX, Package, Snowflake, Clock, AlertTriangle, Sparkles,
  Hash, User as UserIcon, Truck, CreditCard, HelpCircle, Check,
  Camera, Send, Loader2, CheckCircle2, ShieldOff, AlertCircle, X,
  Info, ShieldCheck, ImagePlus,
} from 'lucide-react';
import './PublicDeliveryComplaint.css';
import PublicFooter from '@/components/legal/PublicFooter';
import LegalIconMenu from '@/components/legal/LegalIconMenu';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'missing_product',  label: 'Brak produktu',        icon: Package },
  { id: 'wrong_product',    label: 'Niewłaściwy produkt',  icon: PackageX },
  { id: 'cold_food',        label: 'Zimne jedzenie',       icon: Snowflake },
  { id: 'late_delivery',    label: 'Opóźnienie dostawy',   icon: Clock },
  { id: 'damaged_spilled',  label: 'Uszkodzone / rozlane', icon: AlertTriangle },
  { id: 'quality_issue',    label: 'Jakość jedzenia',      icon: Sparkles },
  { id: 'wrong_quantity',   label: 'Zła ilość',            icon: Hash },
  { id: 'driver_issue',     label: 'Problem z kierowcą',   icon: UserIcon },
  { id: 'payment_issue',    label: 'Problem z płatnością', icon: CreditCard },
  { id: 'other',            label: 'Inne',                 icon: HelpCircle },
];
const ORDER_METHODS = [
  { id: 'phone',           label: 'Telefonicznie' },
  { id: 'website',         label: 'Strona internetowa — mrjerry.pl' },
  { id: 'app',             label: 'Aplikacja' },
  { id: 'pyszne',          label: 'Pyszne.pl' },
  { id: 'glovo',           label: 'Glovo' },
  { id: 'uber_eats',       label: 'Uber Eats' },
  { id: 'external_portal', label: 'Inny portal zewnętrzny' },
  { id: 'other',           label: 'Inny' },
];

const PublicDeliveryComplaint = () => {
  const { location = 'nowysacz' } = useParams();
  const [cfg, setCfg] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [f, setF] = useState({
    customerName: '', customerPhone: '', customerEmail: '', consentContact: false,
    orderNumber: '', orderDate: '', orderTime: '', deliveryAddress: '',
    orderMethod: '', externalPortalName: '',
    categories: [], categoryOther: '', description: '', expectedResolution: '',
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const photoInput = useRef(null);
  const cameraInput = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/public-delivery-complaints/config?location=${location}`)
      .then((r) => { setCfg(r.data); setEnabled(r.data?.enabled !== false); })
      .catch(() => setCfg({ welcomeText: '', confirmationText: '' }));
  }, [location]);

  const upd = (patch) => setF((prev) => ({ ...prev, ...patch }));
  const toggleCat = (id) =>
    setF((p) => ({ ...p, categories: p.categories.includes(id) ? p.categories.filter((c) => c !== id) : [...p.categories, id] }));

  const validate = () => {
    if (!f.customerName.trim() || f.customerName.trim().length < 2) return 'Podaj imię i nazwisko';
    if (!f.customerPhone.trim() && !f.customerEmail.trim()) return 'Podaj numer telefonu lub email';
    if (f.customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.customerEmail)) return 'Nieprawidłowy email';
    if (f.customerPhone && !/^[+]?[0-9\s\-()]{7,}$/.test(f.customerPhone)) return 'Nieprawidłowy telefon';
    if (!f.consentContact) return 'Wymagana zgoda na kontakt';
    if (!f.categories.length) return 'Wybierz co najmniej jedną kategorię problemu';
    if (f.description.trim().length < 10) return 'Opis musi mieć co najmniej 10 znaków';
    return null;
  };

  const uploadPhotos = async (files) => {
    if (photos.length + files.length > 5) { setError('Max 5 zdjęć'); return; }
    setUploading(true); setError('');
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('location', location);
        const r = await axios.post(`${API}/api/public-delivery-complaints/photo-upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60_000,
        });
        uploaded.push(r.data);
      }
      setPhotos((p) => [...p, ...uploaded]);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Błąd uploadu');
    } finally { setUploading(false); }
  };

  const submit = async () => {
    setError('');
    const err = validate();
    if (err) { setError(err); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/public-delivery-complaints/submit`, { ...f, location, photos });
      setResult(r.data);
      // Zapisz akceptację zgody RODO (best-effort, nie blokuj flow)
      axios.post(`${API}/api/legal/consents`, {
        context: 'form:delivery-complaint',
        documentTypes: ['regulamin', 'privacy'],
        userEmail: f.customerEmail || null,
        userPhone: f.customerPhone || null,
        formPayloadSummary: `${f.customerName} / ${f.customerPhone || f.customerEmail || '-'}`,
      }).catch(() => {});
    } catch (e) {
      setError(e?.response?.data?.detail || 'Błąd wysyłki');
    } finally { setSubmitting(false); }
  };

  /* --------- SUCCESS SCREEN --------- */
  if (result) {
    return (
      <div className="mjdc-root">
        <div className="mjdc-shell">
          <div className="mjdc-section">
            <div className="mjdc-success">
              <div className="mjdc-success-ring">
                <CheckCircle2 size={48} strokeWidth={2} />
              </div>
              <h1 className="mjdc-success-title">Dziękujemy!</h1>
              <p className="mjdc-success-desc">
                {result.confirmationText || 'Twoje zgłoszenie trafiło do managera restauracji. Skontaktujemy się z Tobą tak szybko, jak to możliwe.'}
              </p>
              <div className="mjdc-badge">
                <div className="mjdc-badge-label">Numer zgłoszenia</div>
                <div className="mjdc-badge-num" data-testid="cx-complaint-number">{result.complaintNumber}</div>
              </div>
              <div className="mjdc-success-hint">
                <Camera size={16} strokeWidth={2} />
                <span>Zrób <strong>zrzut ekranu</strong> z numerem zgłoszenia — przyda się przy kontakcie z restauracją.</span>
              </div>
            </div>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  /* --------- DISABLED SCREEN --------- */
  if (!enabled) {
    return (
      <div className="mjdc-root">
        <div className="mjdc-shell">
          <div className="mjdc-section">
            <div className="mjdc-disabled">
              <div className="mjdc-disabled-icon"><ShieldOff size={38} strokeWidth={1.6} /></div>
              <h1 className="mjdc-success-title" style={{ fontSize: 22 }}>Formularz chwilowo wyłączony</h1>
              <p className="mjdc-success-desc" style={{ margin: 0 }}>
                Skontaktuj się bezpośrednio z restauracją, aby zgłosić problem z zamówieniem.
              </p>
            </div>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  /* --------- MAIN FORM --------- */
  return (
    <div className="mjdc-root" data-testid="public-delivery-complaint">
      <div className="mjdc-shell">
        {/* HERO */}
        <div className="mjdc-hero">
          <div className="mjdc-hero-icon">
            <PackageX size={38} strokeWidth={1.6} />
          </div>
          <div className="mjdc-eyebrow">MR JERRY</div>
          <h1 className="mjdc-title">Zgłoś problem z zamówieniem</h1>
          <p className="mjdc-subtitle">
            {cfg?.welcomeText ||
              'Naprawimy każdy problem. Twoje zgłoszenie trafia bezpośrednio do managera restauracji i wracamy z odpowiedzią w ciągu 24 h.'}
          </p>
        </div>

        {/* 1. Twoje dane */}
        <div className="mjdc-section">
          <h2 className="mjdc-section-title">
            <span className="mjdc-section-num">1</span> Twoje dane
          </h2>
          <div className="mjdc-field">
            <label className="mjdc-label">Imię i nazwisko <span className="req">*</span></label>
            <input className="mjdc-input" value={f.customerName}
              onChange={(e) => upd({ customerName: e.target.value })} data-testid="cx-name" placeholder="Anna Kowalska" />
          </div>
          <div className="mjdc-row mjdc-row-2">
            <div className="mjdc-field">
              <label className="mjdc-label">Telefon</label>
              <input className="mjdc-input" value={f.customerPhone}
                onChange={(e) => upd({ customerPhone: e.target.value })} placeholder="+48 500 600 700" data-testid="cx-phone" />
            </div>
            <div className="mjdc-field">
              <label className="mjdc-label">E-mail</label>
              <input className="mjdc-input" type="email" value={f.customerEmail}
                onChange={(e) => upd({ customerEmail: e.target.value })} placeholder="anna@example.pl" data-testid="cx-email" />
            </div>
          </div>
          <div className="mjdc-hint">Podaj przynajmniej jedną formę kontaktu.</div>

          <div className="mjdc-consent" data-checked={f.consentContact ? 'true' : 'false'}
            onClick={() => upd({ consentContact: !f.consentContact })} role="checkbox"
            aria-checked={f.consentContact} tabIndex={0}>
            <input type="checkbox" checked={f.consentContact} readOnly data-testid="cx-consent" />
            <div className="mjdc-consent-box"><Check size={14} strokeWidth={3} /></div>
            <div className="mjdc-consent-text">
              Wyrażam zgodę na przetwarzanie moich danych osobowych przez administratora
              (MR JERRY JERZY FLOREK, ul. Kościuszki 18, Nowy Sącz, NIP 734-350-41-81) w celu
              rozpatrzenia niniejszej reklamacji oraz kontaktu w tej sprawie zgodnie z{' '}
              <a href="/polityka-prywatnosci" target="_blank" rel="noopener" style={{ color: '#d4af37' }}>Polityką prywatności</a>
              {' '}i{' '}
              <a href="/regulamin" target="_blank" rel="noopener" style={{ color: '#d4af37' }}>Regulaminem</a>. <span className="req">*</span>
            </div>
          </div>
        </div>

        {/* 2. Dane zamówienia */}
        <div className="mjdc-section">
          <h2 className="mjdc-section-title">
            <span className="mjdc-section-num">2</span> Dane zamówienia
          </h2>
          <div className="mjdc-field">
            <label className="mjdc-label">Numer zamówienia / paragonu</label>
            <input className="mjdc-input" value={f.orderNumber}
              onChange={(e) => upd({ orderNumber: e.target.value })} placeholder="np. 1234 lub PAR-2026-01" />
          </div>
          <div className="mjdc-row mjdc-row-2">
            <div className="mjdc-field">
              <label className="mjdc-label">Data</label>
              <input className="mjdc-input" type="date" value={f.orderDate}
                onChange={(e) => upd({ orderDate: e.target.value })} />
            </div>
            <div className="mjdc-field">
              <label className="mjdc-label">Godzina</label>
              <input className="mjdc-input" type="time" value={f.orderTime}
                onChange={(e) => upd({ orderTime: e.target.value })} />
            </div>
          </div>
          <div className="mjdc-field">
            <label className="mjdc-label">Adres dostawy</label>
            <input className="mjdc-input" value={f.deliveryAddress}
              onChange={(e) => upd({ deliveryAddress: e.target.value })} placeholder="ul. Piękna 12, 33-300 Nowy Sącz" />
          </div>
          <div className="mjdc-field">
            <label className="mjdc-label">Sposób złożenia zamówienia</label>
            <select className="mjdc-select" value={f.orderMethod}
              onChange={(e) => upd({ orderMethod: e.target.value, externalPortalName: '' })}>
              <option value="">-- wybierz --</option>
              {ORDER_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          {f.orderMethod === 'external_portal' && (
            <div className="mjdc-field">
              <label className="mjdc-label">Nazwa portalu</label>
              <input className="mjdc-input" value={f.externalPortalName}
                onChange={(e) => upd({ externalPortalName: e.target.value })}
                placeholder="Pyszne, Glovo, Uber Eats…" />
            </div>
          )}
        </div>

        {/* 3. Rodzaj problemu */}
        <div className="mjdc-section">
          <h2 className="mjdc-section-title">
            <span className="mjdc-section-num">3</span> Rodzaj problemu <span className="req" style={{ color: 'var(--gold)' }}>*</span>
          </h2>
          <div className="mjdc-tiles">
            {CATEGORIES.map((c) => {
              const active = f.categories.includes(c.id);
              const Icon = c.icon;
              return (
                <div key={c.id} className="mjdc-tile" data-active={active ? 'true' : 'false'}
                  onClick={() => toggleCat(c.id)} data-testid={`cat-${c.id}`}>
                  <div className="mjdc-tile-icon"><Icon size={18} strokeWidth={1.8} /></div>
                  <div style={{ flex: 1 }}>{c.label}</div>
                  {active && <Check size={16} strokeWidth={3} />}
                </div>
              );
            })}
          </div>
          {f.categories.includes('other') && (
            <div className="mjdc-field" style={{ marginTop: 14 }}>
              <label className="mjdc-label">Doprecyzuj</label>
              <input className="mjdc-input" value={f.categoryOther}
                onChange={(e) => upd({ categoryOther: e.target.value })} placeholder="Opisz krótko…" />
            </div>
          )}

          <div className="mjdc-field" style={{ marginTop: 18 }}>
            <label className="mjdc-label">Opisz dokładnie, co było nie tak <span className="req">*</span></label>
            <textarea className="mjdc-textarea" value={f.description}
              onChange={(e) => upd({ description: e.target.value })} data-testid="cx-desc"
              placeholder="Im więcej szczegółów, tym szybciej znajdziemy rozwiązanie." />
          </div>
          <div className="mjdc-field">
            <label className="mjdc-label">Jakiego rozwiązania oczekujesz? (opcjonalnie)</label>
            <textarea className="mjdc-textarea" value={f.expectedResolution}
              onChange={(e) => upd({ expectedResolution: e.target.value })}
              placeholder="np. zwrot pieniędzy, ponowne przygotowanie, kod rabatowy…" />
            <div className="mjdc-hint">Wybór nie oznacza automatycznej akceptacji reklamacji.</div>
          </div>
        </div>

        {/* 4. Zdjęcia */}
        <div className="mjdc-section">
          <h2 className="mjdc-section-title">
            <span className="mjdc-section-num">4</span> Zdjęcia (opcjonalnie)
          </h2>

          <div className="mjdc-tip">
            <Info size={16} strokeWidth={2} />
            <div>
              <strong>Zdjęcia znacznie zwiększają wiarygodność zgłoszenia.</strong> Jeśli to możliwe,
              dołącz fotografię produktu, opakowania lub paragonu — pomoże nam to szybciej
              zweryfikować sprawę i zaproponować konkretne rozwiązanie.
            </div>
          </div>

          <input ref={photoInput} type="file" accept="image/jpeg,image/png,image/heic,image/heif" multiple
            style={{ display: 'none' }}
            onChange={(e) => { const files = Array.from(e.target.files || []); if (files.length) uploadPhotos(files); e.target.value = ''; }} />
          <input ref={cameraInput} type="file" accept="image/*" capture="environment"
            style={{ display: 'none' }}
            onChange={(e) => { const files = Array.from(e.target.files || []); if (files.length) uploadPhotos(files); e.target.value = ''; }} />

          <div className="mjdc-photo-actions">
            <button type="button" className="mjdc-photo-btn mjdc-photo-btn-camera"
              onClick={() => cameraInput.current?.click()} disabled={uploading || photos.length >= 5}
              data-testid="cx-take-photo">
              <Camera size={18} /> Zrób zdjęcie
            </button>
            <button type="button" className="mjdc-photo-btn"
              onClick={() => photoInput.current?.click()} disabled={uploading || photos.length >= 5}
              data-testid="cx-add-photo">
              {uploading
                ? <><Loader2 size={18} className="mjdc-spin" /> Wgrywam…</>
                : <><ImagePlus size={18} /> Dodaj zdjęcia ({photos.length}/5)</>}
            </button>
          </div>
          {!!photos.length && (
            <div className="mjdc-photo-grid">
              {photos.map((p, i) => (
                <div key={i} className="mjdc-photo-thumb">
                  <img src={`${API}${p.url}`} alt="" />
                  <button type="button" className="mjdc-photo-remove"
                    onClick={() => setPhotos((ph) => ph.filter((_, ix) => ix !== i))} aria-label="Usuń zdjęcie">
                    <X size={12} strokeWidth={3} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ERROR */}
        {error && (
          <div className="mjdc-error" data-testid="cx-error">
            <AlertCircle size={18} /> {error}
          </div>
        )}

        {/* SUBMIT */}
        <button type="button" className="mjdc-submit" onClick={submit} disabled={submitting} data-testid="cx-submit">
          {submitting
            ? <><Loader2 size={18} className="mjdc-spin" /> Wysyłam…</>
            : <><Send size={18} /> Wyślij zgłoszenie</>}
        </button>

        {/* LEGAL / POLICY FOOTER */}
        <div className="mjdc-policy">
          <div className="mjdc-policy-header">
            <ShieldCheck size={16} strokeWidth={2} />
            <span>Nasze zobowiązanie</span>
          </div>
          <p>
            Każdą reklamację rozpatrujemy w ciągu <strong>48 godzin</strong> od momentu złożenia
            zamówienia. Skontaktujemy się z Tobą telefonicznie lub mailowo z propozycją rozwiązania.
          </p>
          <p className="mjdc-policy-note">
            Zastrzegamy sobie prawo do indywidualnej oceny każdego zgłoszenia oraz do prośby o
            dodatkowe informacje w celu potwierdzenia okoliczności reklamacji. Pełne zasady
            znajdziesz w{' '}
            <a href="/regulamin-reklamacji" target="_blank" rel="noopener" style={{ color: '#d4af37', fontWeight: 700 }}>
              Regulaminie reklamacji
            </a>.
          </p>
        </div>
      </div>

      <style>{`
        .mjdc-spin { animation: mjdc-rot 0.8s linear infinite; }
        @keyframes mjdc-rot { to { transform: rotate(360deg); } }
      `}</style>
      <PublicFooter />
      <LegalIconMenu position="bottom-right" />
    </div>
  );
};

export default PublicDeliveryComplaint;
